import { atom } from 'jotai';
import { atomWithMutation, atomWithQuery, type AtomWithQueryResult } from 'jotai-tanstack-query';
import { isAxiosError } from 'axios';

import { instance } from '@/interfaces';
import { getProjectSensors } from '@/interfaces/project';

import type { Getter } from 'jotai';
import type { APIResponse } from '@/@types/types';
import type { Project, ProjectData, ProjectPostData, SensorData } from '@/@types/group';

export const selectedGroupIdAtom = atom('');
export const defaultGroupIdAtom = atom('');

export const projectsAtom = atomWithQuery((get: Getter) => ({
  queryKey: ['PROJECTS', get(selectedGroupIdAtom)],
  queryFn: async ({ queryKey: [, selectedGroupId], signal }) => {
    if (selectedGroupId === '') return [];

    const data = await getProjects(selectedGroupId, signal);
    const devicesArray = await Promise.all(data.map(async (row) => await getProjectSensors(row.id)));
    const projects: Project[] = data.map((row, i) => ({ ...row, devices: devicesArray[i] }));

    return projects;
  },
}));
async function getProjects(groupId: string, signal: AbortSignal) {
  try {
    const { data } = await instance.get<APIResponse<ProjectData[]>>(`/group/${groupId}/project`, { signal });
    return data.data;
  } catch (err) {
    if (isAxiosError<APIResponse>(err)) {
      console.error('[getProjects] API error code:', err.response.data.errorCode);
      return [];
    }
    if (typeof err === 'string' && err.startsWith('INTERCEPTOR')) {
      console.error('[getProjects] interceptor error', err);
      return [];
    }
    console.error('[getProjects] Unknown error', err);
    return [];
  }
}

export const groupSensorsAtom = atomWithQuery((get: Getter) => ({
  queryKey: ['SENSORS', get(selectedGroupIdAtom)],
  queryFn: async ({ queryKey: [, selectedGroupId] }) => {
    if (selectedGroupId === '') return [];

    const data = await getGroupSensors(selectedGroupId);
    return data;
  },
}));
async function getGroupSensors(groupId: string) {
  try {
    const { data } = await instance.get<APIResponse<SensorData[]>>(`/group/${groupId}/sensors`);
    return data.data;
  } catch (err) {
    if (isAxiosError<APIResponse>(err)) {
      console.error('[getGroupSensors] API error code:', err.response.data.errorCode);
      return [];
    }
    console.error('[getGroupSensors] Unknown error', err);
    return [];
  }
}

export const unassignedSensors = atomWithQuery((get: Getter) => ({
  queryKey: ['UNASSIGNED_SENSORS', get(projectsAtom), get(groupSensorsAtom)],
  queryFn: async ({ queryKey: [, projectsAtomValue, groupSensorsAtomValue] }) => {
    const { data: projects } = projectsAtomValue as AtomWithQueryResult<Project[]>;
    const { data: groupSensors } = groupSensorsAtomValue as AtomWithQueryResult<SensorData[]>;

    let result = groupSensors.map((sensor) => ({ id: sensor.id, name: sensor.name }));
    for (const project of projects) {
      for (const device of project.devices) {
        result = result.filter((sensor) => sensor.id !== device.id);
      }
    }

    return result;
  },
}));

export const allGroupSensorsAtom = atomWithQuery(() => ({
  queryKey: ['ALL_GROUP_SENSORS'],
  queryFn: async () => {
    const data = await getAllGroupSensors();
    return data;
  },
}));
async function getAllGroupSensors() {
  try {
    const { data } = await instance.get<APIResponse<SensorData[]>>(`/group/sensors/name`);
    return data.data;
  } catch (err) {
    if (isAxiosError<APIResponse>(err)) {
      console.error('[getAllGroupSensors] API error code:', err.response.data.errorCode);
      return [];
    }
    console.error('[getAllGroupSensors] Unknown error', err);
    return [];
  }
}

type MutateProjectParams = { groupId: string; data: ProjectPostData };
export const mutateProjectAtom = atomWithMutation(() => ({
  mutationFn: async ({ groupId, data }: MutateProjectParams) => {
    const isSuccess = await postProjectData(groupId, data);
    return isSuccess;
  },
}));
async function postProjectData(groupId: string, body: ProjectPostData) {
  try {
    const { data } = await instance.post<APIResponse>(`/group/${groupId}/project`, body);
    if (data.errorCode !== 'OK') {
      console.error('[postProjectData] API error code:', data.errorCode);
      return false;
    }
    return true;
  } catch (err) {
    if (isAxiosError<APIResponse>(err)) {
      console.error('[postProjectData] API error code:', err.response.data.errorCode);
    } else {
      console.error('[postProjectData] Unknown error', err);
    }

    return false;
  }
}

export const deleteGroupAtom = atomWithMutation(() => ({
  mutationFn: async (groupId: string) => {
    const isSuccess = await deleteGroup(groupId);
    return isSuccess;
  },
}));
async function deleteGroup(groupId: string) {
  try {
    const { data } = await instance.delete<APIResponse>(`/group/${groupId}`);
    if (data.errorCode !== 'OK') {
      console.error('[deleteGroup] API error code:', data.errorCode);
      return false;
    }
    return true;
  } catch (err) {
    if (isAxiosError<APIResponse>(err)) {
      console.error('[deleteGroup] API error code:', err.response.data.errorCode);
    } else {
      console.error('[deleteGroup] Unknown error', err);
    }

    return false;
  }
}

type EditGroupNameParams = { groupId: string; groupName: string };
export const editGroupNameAtom = atomWithMutation(() => ({
  mutationFn: async ({ groupId, groupName }: EditGroupNameParams) => {
    const isSuccess = await editGroupName(groupId, groupName);
    return isSuccess;
  },
}));
async function editGroupName(groupId: string, groupName: string) {
  try {
    const { data } = await instance.patch<APIResponse>(`/group/${groupId}`, { groupName });
    if (data.errorCode !== 'OK') {
      console.error('[editGroupName] API error code:', data.errorCode);
      return false;
    }
    return true;
  } catch (err) {
    if (isAxiosError<APIResponse>(err)) {
      console.error('[editGroupName] API error code:', err.response.data.errorCode);
    } else {
      console.error('[editGroupName] Unknown error', err);
    }

    return false;
  }
}
